import { MaterialService } from './material.service';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError, timer, race, TimeoutError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, filter, finalize, map, retry, shareReplay, switchMap, take, timeout } from 'rxjs/operators';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { Router } from '@angular/router';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { AIFeedbackResponse, AIJustificationResponse, AIMediaConnectivityProbe, AIProvidersStatusResponse, AIResults, AIStep, AiTest } from '../model/ai.model';
import { environment } from '../../../../environments/environment';

@Injectable()
export class AiExamService extends KiwixiService {
    // apiAi = 'https://bab24c5e-957d-4e38-87bc-cd55499abd49.app.gra.ai.cloud.ovh.net/' + 'api/application/';
    // apiStagingAi
    apiAi = environment.aiServer + 'api/application/';
    apiBackendAi = environment.server + 'ai/token/';

    /**
     * Jeton du moteur IA, un par test : Engage le lie à un seul test (portée
     * `exam test:<token>`, PLATEFORMS-R20). Mis en cache jusqu'à son expiration.
     */
    private aiAccessTokensByTestToken = new Map<string, { accessToken: string; expiresAt: number }>();
    /** Demandes de jeton en cours : des appels simultanés partagent une seule demande. */
    private aiTokenRequestsInFlight = new Map<string, Observable<string>>();
    // Jeton du test courant (Elate.token ou Lilate.token_certificate) : le
    // backend ne délivre le jeton du moteur IA (`ai/token/`) qu'à son porteur.
    private currentTestToken: string = null;

    /** Attente maximale (ms) avant de redemander un jeton refusé pour excès de requêtes (429). */
    private readonly MAX_TOKEN_RETRY_AFTER_MS = 15000;

    // --- Résilience HTTP des endpoints de génération ---
    // Délai max d'une tentative avant de la considérer comme bloquée. La
    // génération synchrone côté moteur (LLM + TTS) peut être lente, mais le
    // candidat ne doit jamais rester sur un spinner sans fin : au-delà de ce
    // délai on réessaie (borné) puis on remonte l'erreur au composant.
    private readonly GENERATION_TIMEOUT_MS = 60000;
    // Nombre de tentatives supplémentaires sur erreur transitoire (réseau / 5xx
    // / timeout). Les erreurs 4xx (réponse métier) ne sont jamais réessayées.
    private readonly MAX_HTTP_RETRIES = 2;
    // Backoff de base entre deux tentatives (croissance exponentielle).
    private readonly RETRY_BASE_DELAY_MS = 1500;

    /**
     * Indique si une erreur HTTP est transitoire et mérite une nouvelle
     * tentative : timeout RxJS, erreur réseau (status 0) ou erreur serveur
     * (>= 500, dont le 503 `generation_failed`). Les 4xx sont définitives.
     */
    private isRetryableError(err: any): boolean {
        if (err instanceof TimeoutError) {
            return true;
        }
        const status = err?.status;
        return status === 0 || (typeof status === 'number' && status >= 500);
    }

    /**
     * Enveloppe un appel HTTP avec un timeout par tentative et un retry borné à
     * backoff exponentiel sur les seules erreurs transitoires. À chaque retry
     * l'observable source est ré-émis, ce qui relance la requête (le token IA
     * mis en cache est réutilisé). Les erreurs non-retryable sont propagées
     * immédiatement au composant pour affichage.
     */
    private withResilience<T>(source$: Observable<T>, timeoutMs: number = this.GENERATION_TIMEOUT_MS): Observable<T> {
        return source$.pipe(
            timeout({ each: timeoutMs }),
            retry({
                count: this.MAX_HTTP_RETRIES,
                delay: (err, retryCount) => {
                    if (!this.isRetryableError(err)) {
                        return throwError(() => err);
                    }
                    const backoff = this.RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1);
                    return timer(backoff);
                },
            }),
            catchError(err => throwError(() => err)),
        );
    }

    /**
     * Retient le jeton du test en cours. À appeler dès que le test est connu
     * (onboarding) : les appels sans `token` (synthèse vocale, test média)
     * s'en servent pour obtenir le jeton du moteur IA.
     */
    setTestToken(token: string): void {
        if (token) {
            this.currentTestToken = String(token);
        }
    }

    /** Lit le jeton de test d'un payload (objet ou FormData) et le retient. */
    private rememberTestToken(params: any): void {
        const token = params instanceof FormData ? params.get('token') : params?.token;
        if (typeof token === 'string') {
            this.setTestToken(token);
        }
    }

    /** Délai (ms) avant de réessayer après un 429 : `Retry-After`, plafonné. */
    private retryDelayAfterTooManyRequests(err: HttpErrorResponse): number {
        const retryAfterSeconds = Number(err?.headers?.get?.('Retry-After'));
        const requestedMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? retryAfterSeconds * 1000
            : this.RETRY_BASE_DELAY_MS;
        return Math.min(requestedMs, this.MAX_TOKEN_RETRY_AFTER_MS);
    }

    /**
     * Jeton du moteur IA pour un test. Une seule demande à Engage par test tant
     * que le jeton est valide ; `forceRefresh` l'ignore (jeton refusé par le
     * moteur). L'erreur d'Engage remonte telle quelle : le composant lit son
     * `refusal_reason` (ex. `otp_not_verified`).
     */
    private fetchAiToken(testToken: string, forceRefresh = false): Observable<string> {
        const cached = this.aiAccessTokensByTestToken.get(testToken);
        if (!forceRefresh && cached && Date.now() / 1000 < cached.expiresAt - 30) {
            return of(cached.accessToken);
        }
        const inFlight = this.aiTokenRequestsInFlight.get(testToken);
        if (inFlight) {
            return inFlight;
        }
        const request$ = this._http.post<{ access_token: string; expires_at: number }>(
            this.apiBackendAi, { token: testToken }
        ).pipe(
            // Un seul nouvel essai, seulement sur 429 : les autres refus (403 OTP, 404…) sont définitifs.
            retry({
                count: 1,
                delay: (err: HttpErrorResponse) => err?.status === 429
                    ? timer(this.retryDelayAfterTooManyRequests(err))
                    : throwError(() => err),
            }),
            map(res => {
                this.aiAccessTokensByTestToken.set(testToken, {
                    accessToken: res.access_token,
                    expiresAt: res.expires_at,
                });
                return res.access_token;
            }),
            finalize(() => this.aiTokenRequestsInFlight.delete(testToken)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.aiTokenRequestsInFlight.set(testToken, request$);
        return request$;
    }

    /** Le moteur a refusé le jeton (expiré, révoqué) : un jeton neuf peut y remédier. */
    private isAiTokenRejection(err: any): boolean {
        return err?.status === 401 || err?.status === 403;
    }

    /**
     * Exécute `cb` avec le jeton du test courant, lu à l'appel (et non à la
     * souscription). Si le moteur refuse le jeton (401/403), il est redemandé
     * une fois à Engage et l'appel est rejoué ; un second refus remonte tel quel.
     */
    private withAuthHeaders<T>(cb: (headers) => Observable<T>): Observable<T> {
        const testToken = this.currentTestToken;
        if (!testToken) {
            return throwError(() => new Error("Token d'examen manquant pour le moteur IA"));
        }
        const attempt = (forceRefresh: boolean): Observable<T> =>
            this.fetchAiToken(testToken, forceRefresh).pipe(
                switchMap(accessToken => cb({ 'Authorization': 'Bearer ' + accessToken }).pipe(
                    catchError(err => {
                        if (!forceRefresh && this.isAiTokenRejection(err)) {
                            this.aiAccessTokensByTestToken.delete(testToken);
                            return attempt(true);
                        }
                        return throwError(() => err);
                    }),
                )),
            );
        return attempt(false);
    }

    constructor(
        private _http: HttpClient,
        private _router: Router,
        kiwixiGlobals: KiwixiGlobals,
        private materialService: MaterialService,) {
        super(_http, _router, kiwixiGlobals, materialService);
    }

    /**
     * Prépare le test pendant l'onboarding : déclenche la cascade entièrement
     * personnalisée côté moteur (profil enrichi + persona + pools) et prégénère
     * le premier exercice en tâche de fond. La personnalisation doit donc être
     * envoyée ici (dans `params.user.personalization`), pas à startTest.
     * Même payload que startTest. Idempotent côté moteur.
     */
    prepareTest(params): Observable<{ token: string; status: string }> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<{ token: string; status: string }>((headers) =>
            this._http.post<{ token: string; status: string }>(this.apiAi + 'prepare-test/', params, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Statut de préparation du test (polling de la checklist de démarrage).
     * `first_exercise_ready` passe à true dès que le premier exercice est
     * prégénéré (statut `ready`) ou déjà livré.
     */
    getPrepareStatus(token: string): Observable<{ token: string; status: string; first_exercise_ready: boolean }> {
        this.setTestToken(token);
        const params = new HttpParams().set('token', token);
        return this.withAuthHeaders<{ token: string; status: string; first_exercise_ready: boolean }>((headers) =>
            this._http.get<{ token: string; status: string; first_exercise_ready: boolean }>(
                this.apiAi + 'prepare-status/', { headers, params }))
            .pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Attend que le premier exercice prégénéré soit prêt avant de lancer
     * start-test, pour ne pas déclencher inutilement la génération synchrone
     * pendant que le worker travaille encore.
     *
     * Émet une seule valeur puis complète :
     * - true  : exercice prêt (ou test inconnu / erreur de polling — dans
     *   ce cas start-test gère lui-même le fallback synchrone) ;
     * - false : délai d'attente dépassé (fallback synchrone assumé).
     */
    waitForTestReady(token: string, timeoutMs = 25000, intervalMs = 2000): Observable<boolean> {
        const polling$ = timer(0, intervalMs).pipe(
            switchMap(() => this.getPrepareStatus(token).pipe(
                // 404 (prepare-test absent/échoué) ou erreur réseau : ne pas
                // bloquer le candidat, start-test prendra le relais.
                catchError(() => of({ first_exercise_ready: true }))
            )),
            filter(res => !!res.first_exercise_ready),
            map(() => true),
        );
        const deadline$ = timer(timeoutMs).pipe(map(() => false));
        return race(polling$, deadline$).pipe(take(1));
    }

    /**
     * Démarre le test : livre le premier exercice et bascule le test en
     * `ongoing` côté moteur. C'est le seul endpoint qui lance un test (le
     * fallback de création synchrone est géré côté moteur si aucun test
     * n'a été préparé). Même payload que prepareTest.
     */
    startTest(params): Observable<AiTest> {
        this.rememberTestToken(params);
        return this.withResilience(
            this.withAuthHeaders<AiTest>((headers) => this._http.post<AiTest>(this.apiAi + 'start-test/', params, { headers })),
        );
    }

    /**
     * Abandon volontaire du test par le candidat (idempotent côté moteur).
     * Le moteur calcule les résultats partiels et notifie Engage par webhook
     * (reason=user_initiated) : le front n'a aucun statut à patcher.
     */
    abandonTest(params: { token: string }): Observable<{ token: string; status: string }> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<{ token: string; status: string }>((headers) =>
            this._http.post<{ token: string; status: string }>(this.apiAi + 'abandon-test/', params, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    generateStep(params): Observable<AIStep> {
        this.rememberTestToken(params);
        return this.withResilience(
            this.withAuthHeaders<AIStep>((headers) => this._http.post<AIStep>(this.apiAi + 'generate-step/', params, { headers })),
        );
    }

    generateStepFromAudio(params): Observable<AIStep> {
        this.rememberTestToken(params);
        return this.withResilience(
            this.withAuthHeaders<AIStep>((headers) => this._http.post<AIStep>(this.apiAi + 'generate-step-from-audio/', params, { headers })),
        );
    }

    generateLastStep(params): Observable<AIStep> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<AIStep>((headers) => this._http.post<AIStep>(this.apiAi + 'generate-last-step/', params, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    getTestResults(params): Observable<AIResults> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<AIResults>((headers) => this._http.post<AIResults>(this.apiAi + 'test-results/', params, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    reloadTestResults(params): Observable<AIResults> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<AIResults>((headers) => this._http.post<AIResults>(this.apiAi + 'reload-test-results/', params, { headers }));
    }

    /**
     * Récupère la justification globale d'un test.
     * Endpoint : POST test-justification/ { token }
     */
    getTestJustification(params: { token: string }): Observable<AIJustificationResponse> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<AIJustificationResponse>((headers) =>
            this._http.post<AIJustificationResponse>(this.apiAi + 'test-justification/', params, { headers })
        ).pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Récupère le feedback d'un test. Le feedback est généré de façon asynchrone
     * après le passage du test à `done`.
     * Endpoint : POST test-feedback/ { token }
     */
    getTestFeedback(params: { token: string }): Observable<AIFeedbackResponse> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<AIFeedbackResponse>((headers) =>
            this._http.post<AIFeedbackResponse>(this.apiAi + 'test-feedback/', params, { headers })
        ).pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Polle test-feedback jusqu'à ce que la génération soit terminée
     * (`ready` ou `failed`) ou jusqu'au délai max ; émet alors la réponse.
     */
    waitForFeedbackReady(token: string, timeoutMs = 120000, intervalMs = 3000): Observable<AIFeedbackResponse> {
        return this.pollUntilReady<AIFeedbackResponse>(
            () => this.getTestFeedback({ token }),
            (res) => !!res && (res.feedback_status === 'ready' || res.feedback_status === 'failed'),
            timeoutMs,
            intervalMs,
        );
    }

    /**
     * Polle test-justification jusqu'à ce que la génération soit terminée
     * (`ready` ou `failed`) ou jusqu'au délai max ; émet alors la réponse.
     */
    waitForJustificationReady(token: string, timeoutMs = 120000, intervalMs = 3000): Observable<AIJustificationResponse> {
        return this.pollUntilReady<AIJustificationResponse>(
            () => this.getTestJustification({ token }),
            (res) => !!res && (res.justification_status === 'ready' || res.justification_status === 'failed'),
            timeoutMs,
            intervalMs,
        );
    }

    /**
     * Helper générique de polling : interroge `fetch` immédiatement puis toutes
     * les `intervalMs` jusqu'à ce que `isDone` soit vrai (émet alors la réponse),
     * ou jusqu'à `timeoutMs` (émet la dernière réponse obtenue, si elle existe).
     */
    private pollUntilReady<T>(
        fetch: () => Observable<T>,
        isDone: (res: T) => boolean,
        timeoutMs: number,
        intervalMs: number,
    ): Observable<T> {
        let last: T;
        const poll$ = timer(0, intervalMs).pipe(
            switchMap(() => fetch().pipe(catchError(() => of(last)))),
            map((res) => { last = res; return res; }),
            filter((res) => isDone(res)),
            take(1),
        );
        const timeout$ = timer(timeoutMs).pipe(map(() => last));
        return race(poll$, timeout$);
    }

    getCurrentTest(token: string, options?: { userRetry?: boolean }): Observable<AiTest> {
        this.setTestToken(token);
        const body: { token: string; user_retry?: boolean } = { token };
        if (options?.userRetry) {
            body.user_retry = true;
        }
        return this.withAuthHeaders<AiTest>((headers) =>
            this._http.post<AiTest>(this.apiAi + 'reload-current-step/', body, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    reformulateQuestion(params): Observable<any> {
        this.rememberTestToken(params);
        return this.withAuthHeaders<any>((headers) => this._http.post<any>(this.apiAi + 'reformulate/', params, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Synthèse vocale. Le jeton du moteur est lié au test : le jeton de test
     * courant accompagne la requête pour que le moteur le vérifie.
     */
    generateSpeech(params): Observable<any> {
        this.rememberTestToken(params);
        const body = this.currentTestToken ? { ...params, token: this.currentTestToken } : params;
        return this.withAuthHeaders<any>((headers) => this._http.post<any>(this.apiAi + 'generate-speech/', body, { headers }))
            .pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Récupère l'URL présignée de l'audio de test de connectivité.
     *
     * L'audio vit dans le bucket d'examen, dans la langue du test, et est signé
     * exactement comme les médias générés pendant l'examen.
     */
    getMediaConnectivityProbe(language?: string): Observable<AIMediaConnectivityProbe> {
        let params = new HttpParams();
        if (language) {
            params = params.set('language', language);
        }
        if (this.currentTestToken) {
            // Le jeton du moteur est lié au test : le moteur vérifie le token de la requête.
            params = params.set('token', this.currentTestToken);
        }
        return this.withAuthHeaders<AIMediaConnectivityProbe>((headers) =>
            this._http.get<AIMediaConnectivityProbe>(this.apiAi + 'media-connectivity-probe/', {
                headers,
                params,
            }))
            .pipe(catchError(err => throwError(() => err)));
    }

    /**
     * Récupère le statut des fournisseurs IA via l'endpoint externe (pas d'auth requise, token en query param)
     * Endpoint : GET ai-providers-status/?token=<token>
     */
    getProvidersStatus(): Observable<AIProvidersStatusResponse> {
        return this._http.get<AIProvidersStatusResponse>(
            environment.server + 'ai-providers-status/'
        ).pipe(catchError(err => throwError(() => err)));
    }
}
